import { useState, useEffect } from 'react';
import { X, RefreshCw, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';
import { forceUpdate } from '../../serviceWorkerRegistration';

const FALLBACK_VERSION = process.env.REACT_APP_VERSION || '1.2.0';
const VERSION_STORAGE_KEY = 'destiny_app_version';
const DISMISSED_VERSION_KEY = 'destiny_dismissed_version';

export default function AppUpdateNotification({ user }) {
  const [showBanner, setShowBanner] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [newVersionAvailable, setNewVersionAvailable] = useState(false);
  const [serverVersion, setServerVersion] = useState(FALLBACK_VERSION);
  const [versionNotes, setVersionNotes] = useState("");

  useEffect(() => {
    // Show to all users (including parents and teachers)
    // Note: If user is null (not logged in), still show the banner

    const checkVersion = (currentVersion) => {
      const storedVersion = localStorage.getItem(VERSION_STORAGE_KEY);
      const dismissedVersion = localStorage.getItem(DISMISSED_VERSION_KEY);

      // If this is a new version and user hasn't dismissed it
      if (storedVersion && storedVersion !== currentVersion && dismissedVersion !== currentVersion) {
        setShowBanner(true);
        setNewVersionAvailable(true);
      }

      // Store current version
      localStorage.setItem(VERSION_STORAGE_KEY, currentVersion);
    };

    // Dapatkan versi dari server (ditetapkan manual oleh superadmin)
    const fetchServerVersion = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/app-name`);
        const data = await res.json();
        const version = (data.app_version || '').trim() || FALLBACK_VERSION;
        setServerVersion(version);
        setVersionNotes((data.app_version_notes || '').trim());
        checkVersion(version);
      } catch (e) {
        checkVersion(FALLBACK_VERSION);
      }
    };

    fetchServerVersion();
    // Semak semula setiap 5 minit supaya pengguna aktif turut dapat notifikasi
    const interval = setInterval(fetchServerVersion, 5 * 60 * 1000);

    // Listen for service worker updates
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data?.type === 'SW_UPDATED') {
          setShowBanner(true);
          setNewVersionAvailable(true);
        }
      });

      // Also check registration for waiting worker
      navigator.serviceWorker.getRegistration().then((registration) => {
        if (registration?.waiting) {
          setShowBanner(true);
          setNewVersionAvailable(true);
        }
      });
    }

    return () => clearInterval(interval);
  }, []); // Run once on mount - no dependency on user

  const handleUpdate = () => {
    setIsUpdating(true);
    localStorage.removeItem(DISMISSED_VERSION_KEY);
    
    // Force update
    setTimeout(() => {
      forceUpdate();
    }, 500);
  };

  const handleDismiss = () => {
    setShowBanner(false);
    localStorage.setItem(DISMISSED_VERSION_KEY, serverVersion);
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 z-[9999] animate-slideUp" data-testid="app-update-banner">
      <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white px-4 py-3 rounded-2xl shadow-2xl max-w-md mx-auto sm:mx-0">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center animate-bounce">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <p className="font-bold text-sm">
                🎉 Versi Baru Tersedia!
              </p>
              <p className="text-xs opacity-90">
                v{serverVersion} - Tap untuk kemas kini
              </p>
              {versionNotes && (
                <p className="text-xs opacity-95 mt-1 max-w-[240px] whitespace-pre-wrap" data-testid="app-update-notes">
                  {versionNotes}
                </p>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              onClick={handleUpdate}
              disabled={isUpdating}
              data-testid="app-update-btn"
              size="sm"
              className="bg-white text-purple-600 hover:bg-gray-100 font-bold rounded-full px-4 shadow-lg"
            >
              {isUpdating ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                "Kemas Kini"
              )}
            </Button>
            
            <button
              onClick={handleDismiss}
              data-testid="app-update-dismiss-btn"
              className="p-1.5 hover:bg-white/20 rounded-full transition-colors"
              title="Tutup"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
      
      <style>{`
        @keyframes slideUp {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slideUp {
          animation: slideUp 0.4s ease-out forwards;
        }
      `}</style>
    </div>
  );
}
